<template>
  <div class="bg-white border border-gray-100 mb-80 mr-8 relative rounded-8">
    <div class="overflow-x-auto">
      <table class="w-full border-collapse min-w-[1000px]">
        <thead class="h-56 text-16">
          <tr>
            <th class="w-56 min-w-56 max-w-56" />
            <template v-for="column in selectedComparisonTableColumns" :key="column.key">
              <th class="w-[1fr] border-l border-gray-50">
                <InvestigationTitle
                  :title="column.title"
                  :active-sorting="sortingName == column?.sorting"
                  :ascending="sortingAscending"
                  :sortby="column?.sorting"
                  @sort="handleSortChange($event)"
                />
              </th>
            </template>
            <th class="w-64 min-w-64 max-w-64 border-l border-b border-gray-50 right-0 sticky bg-white z-30">
              <SelectColumns />
            </th>
          </tr>
        </thead>

        <tbody class="text-13 relative">
          <SparkSpinner v-if="loading" custom="absolute left-1/2" />
          <tr v-else v-for="(processChainId, index) in sortedChainIds" :key="processChainId" class="h-64">
            <td v-if="!processChains[processChainId]" colspan="8">
              <div class="ml-16 text-gray-500">Deleted</div>
            </td>
            <template v-else>
              <td
                class="min-w-56 max-w-56 border border-gray-50 hover:bg-gray-50 hover:cursor-pointer mr-auto"
                @click="selectProcessChain(processChainId)"
              >
                <div class="flex items-center justify-center">
                  <input
                    type="checkbox"
                    :checked="activeInvestigationDetails?.uid === processChainId"
                    class="h-16 w-16 rounded-6 border-gray-300 text-primary-600 focus:ring-primary-500"
                    @change.prevent
                  />
                </div>
              </td>
              <template v-for="column in selectedComparisonTableColumns" :key="column.key">
                <td
                  class="w-[300px] min-w-[100px] max-w-[500px] border border-gray-50 hover:bg-gray-50 hover:cursor-pointer"
                  @mouseenter="setMouseHover(processChainId, column?.sorting)"
                  @mouseleave="clearMouseHover(processChainId)"
                >
                  <component
                    :is="column.component"
                    :chain="processChains[processChainId]"
                    editable
                    :mouse-hover="mouseHover[processChainId]"
                    class="w-full"
                  />
                </td>
              </template>

              <td
                class="min-w-64 max-w-64 border border-gray-50 right-0 sticky bg-white text-right"
                :style="{ 'z-index': 20 - index }"
              >
                <Checkout :chain="processChains[processChainId]" />
              </td>
            </template>
          </tr>
          <tr>
            <td colspan="7" :title="lockedForUser ? lockedTitle : ''">
              <div
                :class="lockedForUser ? 'text-gray-700' : 'hover:cursor-pointer hover:text-primary-600'"
                class="font-bold p-12 w-fit flex items-center"
                @click="handleAddProductionOption"
              >
                <SparkButton v-if="sortedChainIds.length === 0" variant="secondary" :disabled="lockedForUser">
                  <i v-if="lockedForUser" class="fas fa-lock pr-4" /><i v-else class="fas fa-plus pr-4" />Add production
                  option
                </SparkButton>
                <div v-else>
                  <i v-if="lockedForUser" class="fas fa-lock" /><i v-else class="fas fa-plus" /> Add production option
                </div>
                <span
                  v-if="isVolumeInvalid"
                  class="ml-4 text-11 bg-red-50 p-2 rounded-8 mr-12 text-red-500 font-normal"
                >
                  <i class="fa-solid fa-circle-exclamation mr-2" /> Warning: Results may be invalid due to a corrupted
                  or incomplete input file.
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <CreateProcessChainModal :show="showCreateProcessChainModal" @close="closeModal" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';

import SparkButton from '../../../SparkComponents/SparkButton.vue';

import CreateProcessChainModal from './CreateProcessChainModal.vue';
import InvestigationTitle from './InvestigationTitle.vue';

import SelectColumns from '@/components/PartWorkflow/Comparison/components/SelectColumns.vue';
import Checkout from '@/components/PartWorkflow/Comparison/tablecells/actions/Checkout.vue';
import CarbonFootprint from '@/components/PartWorkflow/Comparison/tablecells/carbon/CarbonFootprint.vue';
import Cost from '@/components/PartWorkflow/Comparison/tablecells/cost/Cost.vue';
import Feasibility from '@/components/PartWorkflow/Comparison/tablecells/feasibility/Feasibility.vue';
import Leadtime from '@/components/PartWorkflow/Comparison/tablecells/leadtime/Leadtime.vue';
import LotSize from '@/components/PartWorkflow/Comparison/tablecells/lotsize/LotSize.vue';
import ProcessChainName from '@/components/PartWorkflow/Comparison/tablecells/name/ProcessChainName.vue';
import Price from '@/components/PartWorkflow/Comparison/tablecells/price/Price.vue';
import TrafficSignal from '@/components/PartWorkflow/Comparison/tablecells/trafficsignal/TrafficSignal.vue';
import SparkCheckbox from '@/components/SparkComponents/SparkCheckbox.vue';
import { analysisFinished } from '@/composables/processChainHandling.js';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';

const SORT_TYPES = {
  COST: 'cost_pl',
  COST_FULL: 'cost.cost_pl',
  MARKET_PRICE: 'market_price_pl',
  MARKET_PRICE_FULL: 'market_price.market_price_pl',
};

export default {
  name: 'InvestigationTable',

  components: {
    ProcessChainName,
    LotSize,
    Feasibility,
    Cost,
    Leadtime,
    CarbonFootprint,
    Checkout,
    InvestigationTitle,
    CreateProcessChainModal,
    SparkButton,
    SelectColumns,
    SparkCheckbox,
    Price,
    TrafficSignal,
    SparkSpinner,
  },

  data() {
    return {
      sortedChainIds: [],
      mouseHover: {},
      lastSortingCriteria: {
        sortBy: null,
        ascending: null,
      },
      showCreateProcessChainModal: false,
      loading: true,
    };
  },

  computed: {
    ...mapState(['part', 'investigationDetails', 'user']),
    ...mapState('application', ['lockedTitle', 'axiosInstance']),
    ...mapGetters(['lockedForUser']),

    previousPageWasOverview() {
      const previousRoute = this.$router.options.history.state.back;
      if (!previousRoute) return false;
      return previousRoute.toString().includes('/ov/');
    },

    primaryProcessChainId() {
      return this.part?.primary_process_chain_id;
    },

    processChains() {
      return this.part?.process_chains || {};
    },

    processChainIds() {
      return Object.keys(this.processChains || {});
    },

    activeInvestigationDetails() {
      return this.investigationDetails;
    },

    isVolumeInvalid() {
      return this.part?.mesh_checks?.volume_area_trust_status === 'fail';
    },

    sortingName() {
      return this.part?.sorting_name;
    },

    sortingAscending() {
      return this.part?.sorting_ascending;
    },

    comparisonTableColumns() {
      return this.user?.user_settings?.comparison_table_columns || {};
    },

    selectedComparisonTableColumns() {
      const selectedColumns = Object.values(this.comparisonTableColumns || {})
        .filter(column => column.selected === true)
        .sort((a, b) => a.index - b.index);

      return selectedColumns;
    },
  },

  watch: {
    processChains: {
      handler(newProcessChains, oldProcessChains) {
        this.handleProcessChainsChange(newProcessChains, oldProcessChains);
      },
      deep: true,
    },
    primaryProcessChainId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          // Always re-apply current sorting when primary chain changes
          // This ensures primary chain gets priority regardless of current sort
          const { sortBy, ascending } = this.lastSortingCriteria;
          this.applySorting(sortBy || this.sortingName, ascending ?? this.sortingAscending);
        }
      },
    },
  },

  mounted() {
    this.initializeSorting();
    if (this.processChains) this.loading = false;
  },

  beforeMount() {
    this.resetInvestigationDetailsIfNeeded();
  },

  methods: {
    ...mapMutations(['changeInvestigationDetails', 'setProcessChainSorting']),

    selectProcessChain(uid) {
      if (!uid) return;
      this.changeInvestigationDetails({ uid });
    },

    handleAddProductionOption() {
      if (this.lockedForUser) return;

      this.resetInvestigationDetails();
      this.showCreateProcessChainModal = true;
    },

    closeModal() {
      this.showCreateProcessChainModal = false;
    },

    handleSortChange(event) {
      if (!event?.sortby) {
        return;
      }

      this.updatePartSorting(event);
      this.applySorting(event.sortby, event.ascending);
    },

    setMouseHover(processChainId, sorting) {
      if (!processChainId) return;
      this.mouseHover = { ...this.mouseHover, [processChainId]: sorting || '' };
    },

    clearMouseHover(processChainId) {
      if (!processChainId) return;
      this.mouseHover = { ...this.mouseHover, [processChainId]: '' };
    },

    handleProcessChainsChange(newProcessChains, oldProcessChains) {
      const newIds = Object.keys(newProcessChains);
      const oldIds = Object.keys(oldProcessChains);

      const hasStructuralChanges = newIds.length !== oldIds.length || !newIds.every(id => oldIds.includes(id));

      if (hasStructuralChanges) {
        const { sortBy, ascending } = this.lastSortingCriteria;
        this.applySorting(sortBy || this.sortingName, ascending ?? this.sortingAscending);
      } else {
        this.preserveExistingOrder();
      }
    },

    preserveExistingOrder() {
      const currentIds = this.processChainIds;
      const filteredIds = this.sortedChainIds.filter(id => currentIds.includes(id));
      const newIds = currentIds.filter(id => !this.sortedChainIds.includes(id));

      this.sortedChainIds = [...filteredIds, ...newIds];
    },

    applySorting(sortBy, ascending) {
      this.lastSortingCriteria = { sortBy, ascending };
      this.resetInvestigationDetails();

      if (!sortBy) {
        this.sortByPrimaryChain();
        return;
      }

      this.sortByColumn(sortBy, ascending);
    },

    sortByColumn(sortBy, ascending) {
      const sortingValues = this.calculateSortingValues(sortBy);
      const sortedEntries = this.sortEntries(sortingValues, ascending);
      let sortedIds = sortedEntries.map(entry => entry[0]);

      this.sortedChainIds = sortedIds;
    },

    calculateSortingValues(sortBy) {
      const sortingValues = {};

      this.processChainIds.forEach(chainId => {
        const processChain = this.processChains[chainId];
        if (!processChain) {
          sortingValues[chainId] = null;
          return;
        }

        sortingValues[chainId] = this.extractSortingValue(processChain, sortBy);
      });

      return sortingValues;
    },

    extractSortingValue(processChain, sortBy) {
      try {
        switch (sortBy) {
          case SORT_TYPES.COST:
          case SORT_TYPES.COST_FULL:
            return processChain.cost?.cost_pl ?? null;

          case SORT_TYPES.MARKET_PRICE:
          case SORT_TYPES.MARKET_PRICE_FULL:
            return (
              processChain.market_price?.market_price_pl?.user ??
              processChain.market_price?.market_price_pl?.calculated ??
              null
            );

          default:
            return this.getNestedValue(processChain, sortBy);
        }
      } catch (error) {
        console.warn(`Error extracting sorting value for ${sortBy}:`, error);
        return null;
      }
    },

    getNestedValue(obj, path) {
      if (!path || !obj) return null;

      return (
        path.split('.').reduce((current, segment) => {
          return current?.[segment];
        }, obj) ?? null
      );
    },

    sortEntries(sortingValues, ascending) {
      return Object.entries(sortingValues).sort((a, b) => {
        const [, valueA] = a;
        const [, valueB] = b;

        return this.compareValues(valueA, valueB, ascending);
      });
    },

    compareValues(valueA, valueB, ascending) {
      if (valueA === null && valueB === null) return 0;
      if (valueA === null) return 1;
      if (valueB === null) return -1;

      let comparison = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
      } else {
        const stringA = String(valueA).toLowerCase();
        const stringB = String(valueB).toLowerCase();
        comparison = stringA.localeCompare(stringB);
      }

      return ascending ? comparison : -comparison;
    },

    sortByPrimaryChain() {
      const orderedIds = [...this.processChainIds];

      if (this.primaryProcessChainId && orderedIds.includes(this.primaryProcessChainId)) {
        const filteredIds = orderedIds.filter(id => id !== this.primaryProcessChainId);
        this.sortedChainIds = [this.primaryProcessChainId, ...filteredIds];
      } else {
        this.sortedChainIds = orderedIds;
      }
    },

    updatePartSorting(event) {
      if (!this.part?.part_id) {
        console.error('Cannot update sorting: part_id is missing');
        return;
      }

      this.setProcessChainSorting({
        sorting_name: event.sortby,
        sorting_ascending: event.ascending,
      });

      this.axiosInstance
        .put(`/api/v1/part/${this.part.part_id}/`, {
          sorting_name: event.sortby,
          sorting_ascending: event.ascending,
        })
        .catch(error => {
          console.error('Failed to update part sorting:', error);
        });
    },

    initializeSorting() {
      if (!this.sortingName) {
        this.sortByPrimaryChain();
      } else {
        this.applySorting(this.sortingName, this.sortingAscending);
      }
    },

    resetInvestigationDetailsIfNeeded() {
      if (!this.previousPageWasOverview) {
        this.resetInvestigationDetails();
      }
    },

    resetInvestigationDetails() {
      this.changeInvestigationDetails('');
    },

    analysisFinished(uid) {
      return analysisFinished(uid);
    },
  },
};
</script>
